import { useState } from "react";
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { far } from '@fortawesome/pro-regular-svg-icons';
import PropTypes from "prop-types";
import { injectIntl, defineMessages } from "react-intl";
import ToggleChatButton, { messages as chatMessages } from "./ToggleChatButton";

// Services
import FeedbackDialog from "./FeedbackDialog";
import { Button } from "react-bootstrap";
import MenuLocaleSelect from "./MenuLocaleSelect";
import Logo from "./Logo";
import useMessages from "../hooks/useMessages";
import useLocalizedLink from "../hooks/useLocalizedLink";
import useUserAccount from "../hooks/useUserAccount";
import useEnv from "../hooks/useEnv";
import MenuCompanySelect from "./MenuCompanySelect";
import useSessionStorage from "../hooks/useSessionStorage";
import useUserAccountSettings from "../hooks/useUserAccountSettings";
import useLiveChat from "../hooks/useLiveChat";

export const messages = defineMessages({
    invoices: {
        id: "Laskut",
        defaultMessage: "Laskut"
    },
    messageCenter: {
        id: "Viestikeskus",
        defaultMessage: 'Viestikeskus'
    },
    logout: {
        id: "Kirjaudu ulos",
        defaultMessage: "Kirjaudu ulos"
    },
    returnToFrontPage: {
        id: "Palaa takaisin etusivulle",
        defaultMessage: "Palaa takaisin etusivulle"
    },
    login: {
        id: "Kirjaudu MyRopo-palveluun",
        defaultMessage: "Kirjaudu MyRopo-palveluun"
    },
    dataPrivacy: {
        id: "Tietosuoja",
        defaultMessage: "Tietosuoja"
    },
    dataPrivacyTitle: {
        id: "Ropo - Tietosuojaseloste",
        defaultMessage: "Ropo - Tietosuojaseloste"
    },
    faq: {
        id: "UKK",
        defaultMessage: "UKK"
    },
    unreadMessages: {
        id: "Lukemattomat viestit",
        defaultMessage: "Lukemattomat viestit"
    },
    closeMenu: {
        id: "Sulje valikko",
        defaultMessage: "Sulje valikko"
    },
    mainMenu: {
        id: "Päävalikko",
        defaultMessage: "Päävalikko"
    },
    accountSettings: {
        id: "Tilin asetukset",
        defaultMessage: "Tilin asetukset"
    },
    addressUpdateRecommended: {
        id: "Päivitä osoitetietosi",
        defaultMessage: "Päivitä osoitetietosi"
    }
});

const Menu = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { getUnreadMessageCount } = useMessages();
    const { getLocalizedLink } = useLocalizedLink();
    const { userIsCompany, userIsAuthenticated, userName, userHasAccount } = useUserAccount();
    const { addressUpdateIsRecommended } = useUserAccountSettings();
    const lng = props.intl.formatMessage;
    const userAuthorized = userIsAuthenticated();
    const [toggleFeedbackDialog, setToggleFeedbackDialog] = useState(false);
    const privacyLink = getLocalizedLink('LINK_PRIVACY');
    const faqLink = getLocalizedLink('LINK_FAQ');
    const { isEnvTruthy } = useEnv();
    const { setjobTokenUuid } = useSessionStorage();
    const { liveChatParameters } = useLiveChat();

    const links = [
        {
            label: <span aria-label={lng(messages.invoices)}>{lng(messages.invoices)}</span>,
            link: '/jobsearch',
            notifications: 0,
            onClickFunc: props.menuToggle
        },
        {
            label: <span aria-label={lng(messages.messageCenter)}>{lng(messages.messageCenter)}</span>,
            link: '/messagecenter',
            notifications: getUnreadMessageCount(),
            onClickFunc: props.menuToggle
        }
    ];

    // Is customer feedback on or off
    const feedbackEnabled = isEnvTruthy('FEEDBACK_TOGGLE');
    // Is message center on or off
    const messageCenter = isEnvTruthy('MESSAGECENTER');

    // remove message center from menu if it is disabled
    if (!messageCenter) {
        const index = links.findIndex((menuitem) => menuitem.link === '/messagecenter');
        links.splice(index, 1);
    }

    const menuItems = links.map(function (link) {

        let badge = "";

        if (link.notifications > 0) {
            badge = <span className="notification-badge" aria-label={`${lng(messages.unreadMessages)} ${link.notifications}`}>{link.notifications}</span>;
        }

        return (
            <li key={link.link} className="nav-item mb-2">
                <NavLink className="position-relative"
                         to={link.link} onClick={() => link?.onClickFunc ? link.onClickFunc() : ''}
                >
                    {link.label} {badge}
                </NavLink>
            </li>
        );
    });

    /**
     * Log out user and close and hide chat
     */
    function confirmLogout() {
        navigate("/logout");
    }

    /**
     * Open feedback dialog when logging out.
     */
    function handleLogOut() {
        if (feedbackEnabled) {
            setToggleFeedbackDialog(state => !state);
        } else {
            confirmLogout();
        }
    }

    /**
     * Redirect user to login page via logout.
     */
    function handleLogin() {
        sessionStorage.clear();
        const url = location.pathname;
        const jobTokenUuid = url.replace("/job/", "");
        setjobTokenUuid(jobTokenUuid);
        navigate("/");
    }

    return (
        <div id="menu-container">
            <nav id="menu" className={"d-flex flex-column flex-shrink-0 bg-white text-white offcanvas-xl offcanvas-start h-auto " + (props.show ? "show" : "")} aria-label={lng(messages.mainMenu)} role="navigation">
                <div className="d-flex flex-column">
                    {userAuthorized && userIsCompany() && (
                        <MenuCompanySelect />
                    )}
                    <NavLink to={userAuthorized ? "/jobsearch": "#"}
                             className="navbar-brand order-first order-xl-0"
                             onClick={props.menuToggle}
                             aria-label={ lng(messages.returnToFrontPage) }
                             tabIndex={0}
                    >
                        <Logo ariaLabel={lng(messages.returnToFrontPage)} height={34} width={128} />
                    </NavLink>
                    {userAuthorized && !userIsCompany() && (
                        <div className="menu-username">
                            {userName()}
                        </div>
                    )}
                    <ul className="menu nav nav-pills flex-column mr-5">
                        <li className="nav-item-small">
                            {!userAuthorized && (
                                <Button type="button" className={"actionlist-button"} variant='tertiary-menu'
                                        onClick={() => handleLogin()} aria-label={lng(messages.login)} role="menuitem">
                                    <strong>{lng(messages.login)}</strong>
                                </Button>
                            )}
                        </li>
                        {userAuthorized && menuItems}
                    </ul>
                </div>
                <div>
                    <ul className="menu nav nav-pills flex-column" id="menu-bottom">
                        {userAuthorized && liveChatParameters !== null && (
                            <li className="nav-item mb-2">
                                <ToggleChatButton
                                    visitor={{ name: userName(), email: '' }}
                                    parameters={liveChatParameters}
                                    location={'menu'}
                                    aria-label={lng(chatMessages.toggleChat)}
                                    role="menuitem"
                                />
                            </li>
                        )}
                    </ul>
                    <ul className="menu nav nav-pills flex-column" id="menu-footer">
                        {userHasAccount && userAuthorized && (
                            <li className="nav-item-small mb-2">
                                <NavLink
                                    className="position-relative"
                                    to="/user/settings"
                                    onClick={props.menuToggle}
                                >
                                    {lng(messages.accountSettings)}
                                    {addressUpdateIsRecommended() && (
                                        <div
                                            className="settings-badge"
                                            aria-label={lng(messages.addressUpdateRecommended)}
                                            title={lng(messages.addressUpdateRecommended)}
                                        />
                                    )}
                                </NavLink>
                            </li>
                        )}
                        {privacyLink && (
                            <li className="nav-item-small mb-2">
                                <a
                                    title={lng(messages.dataPrivacyTitle)} rel="noopener noreferrer"
                                    href={privacyLink}
                                    target="_blank"
                                    className="dataPrivacy-link"
                                    aria-label={lng(messages.dataPrivacy)}
                                    role="menuitem"
                                >
                                    {lng(messages.dataPrivacy)}
                                </a>
                            </li>
                        )}
                        {faqLink && (
                            <li className="nav-item-small mb-2">
                                <a title={lng(messages.faq)}
                                   rel="noopener noreferrer"
                                   href={faqLink}
                                   target="_blank"
                                   className="contactFaq-link"
                                   aria-label={lng(messages.faq)}
                                   role="menuitem"
                                >
                                    {lng(messages.faq)}
                                </a>
                            </li>
                        )}
                        <li className="nav-item mb-2">
                            <MenuLocaleSelect />
                        </li>
                        {userAuthorized && (
                            <li className="nav-item mb-md-4 mb-2">
                                <Button
                                    type="button"
                                    className={"actionlist-button"}
                                    variant='tertiary-menu'
                                    onClick={() => handleLogOut()}
                                    aria-label={lng(messages.logout)}
                                    role="menuitem"
                                >
                                    {lng(messages.logout)}
                                </Button>
                            </li>
                        )}
                    </ul>
                </div>
                <button
                    onClick={props.menuToggle}
                    onKeyDown={props.menuToggle}
                    className="menuclose d-xl-none"
                    aria-label={lng(messages.closeMenu)}
                    aria-controls="menu"
                >
                    <FontAwesomeIcon icon={far.faXmark} />
                </button>
            </nav>
            <div className={"offcanvas-backdrop fade " + (props.show ? "show d-xl-none" : "d-none")}></div>
            {feedbackEnabled && (
                <FeedbackDialog title={lng(messages.logout)} show={toggleFeedbackDialog} handleConfirmDialog={handleLogOut} />
            )}
        </div>
    );
}

Menu.propTypes = {
    'intl': PropTypes.object.isRequired,
    'show': PropTypes.bool.isRequired,
    'menuToggle': PropTypes.func.isRequired,
};

export default injectIntl(Menu);
